import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { ATHENA } from "@/constants/testIds";

const PRODUCTS = [
  {
    slug: "metaboline",
    code: "ATH-101",
    name: "Metaboline®",
    category: "Metabolic Optimizer",
    tag: "Launching '26",
    desc: "This system targets metabolic inertia in sedentary individuals by reducing visceral fat accumulation, enhancing cellular glucose disposal and supporting insulin sensitivity. It also modulates carbohydrate processing and postprandial blood sugar spikes. Together, this optimizes baseline metabolic efficiency and mitigates energy crashes associated with prolonged physical inactivity.",
  },
  {
    slug: "neurelix",
    code: "ATH-204",
    name: "Neurelix™",
    category: "Performance Ergogenic",
    tag: "Launching '26",
    desc: "This high-performance formula drives physical power by enhancing skeletal muscle contractility and accelerating mitochondrial fatty acid oxidation and cellular ATP regeneration. It also elevates plasma nitric oxide to improve muscular vasodilation, blood flow, and oxygen delivery during kinetic exertion and supports sustained neuromuscular focus, while ensuring rapid osmotic rehydration.",
  },
  {
    slug: "vitala",
    code: "ATH-318",
    name: "Vitala Bioactive",
    category: "Micronutrient Baseline",
    tag: "Launching '26",
    desc: "This foundational matrix delivers biologically active, methylated vitamins to streamline cellular methylation pathways without demanding complex liver enzymatic conversion. Fully chelated mineral bisglycinates maximize gastrointestinal absorption and tissue bioavailability while preventing typical local digestive distress. Vegan Essential Amino Acid complex provides an immediate systemic blueprint to support muscle protein synthesis and daily cellular repair.",
  },
  {
    slug: "kinetiq",
    code: "ATH-422",
    name: "Kinetiq Sport",
    category: "Plant Omega",
    tag: "Launching '26",
    desc: "This plant-derived lipids matrix combines premium algal and botanical oils to supply a comprehensive profile of essential fatty acids. This system delivers bioavailable Omega 3 to optimize cognitive function and cardiovascular structural integrity. Other oils provide omegas 5, 6, 7, and 9 to manage systemic oxidative inflammation and reinforce mucosal cell membrane layers.",
  },
  {
    slug: "vivenol",
    code: "ATH-507",
    name: "Vivenol Skin Biotic",
    category: "Cellular Longevity",
    tag: "Launching '26",
    desc: "This advanced formulation stabilizes cellular aging markers by activating critical sirtuin longevity pathways. Highly bioavailable compounds provide direct antioxidant defense, helping clear senescent cells and managing inflammatory cytokines. This system also offers telomere protective mechanics to support ongoing chromosomal integrity and macromolecular health.",
  },
];

export const Products = () => {
  return (
    <section
      id="products"
      data-testid={ATHENA.productsSection}
      className="relative pt-10 md:pt-14 pb-10 md:pb-14 border-t border-[#D5D1C8] bg-[#F8F7F4]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <span className="label-mono">// PRODUCT PIPELINE</span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.02] tracking-tight text-[#0A1713]">
              Solutions in the
              <br />
              market & on the bench.
            </h2>
          </div>
          <p className="max-w-md text-[#3D4F46]">
            Our portfolio spans commercial nutraceuticals and contract
            development engagements. Below is a representative selection of
            active programs.
          </p>
        </div>

        <ul className="border-t border-[#0A1713]/15">
          {PRODUCTS.map((p, i) => (
            <motion.li
              key={p.slug}
              data-testid={ATHENA.productItem(p.slug)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="group grid grid-cols-12 gap-4 md:gap-6 items-start py-8 md:py-10 border-b border-[#0A1713]/15 hover:bg-[#EAE7E0]/60 transition-colors px-2"
            >
              <div className="col-span-2 md:col-span-1 label-mono pt-2">{p.code}</div>
              <div className="col-span-10 md:col-span-3">
                <div className="font-display text-2xl md:text-3xl text-[#0A1713] leading-tight">
                  {p.category}
                </div>
              </div>
              <div className="col-span-12 md:col-span-6 text-[#3D4F46] leading-relaxed text-sm md:text-base text-justify line-clamp-3 group-hover:line-clamp-none transition-[max-height] duration-500 ease-out">
                {p.desc}
              </div>
              <div className="col-span-6 md:col-span-1 text-xs font-mono uppercase tracking-widest text-[#3D4F46] pt-2">
                {p.tag}
              </div>
              <div className="col-span-6 md:col-span-1 flex justify-end pt-2">
                <Plus
                  size={20}
                  strokeWidth={1.4}
                  className="text-[#0A1713] group-hover:rotate-90 transition-transform duration-500"
                />
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Products;
